import styled, { keyframes } from "styled-components";
import { COLORS, INDIGO_CSS_VARS } from "../../common/colors";

// ─── Keyframes ─────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

export const kSlideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to   { opacity: 1; transform: translateX(0);     }
`;

export const kScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1);    }
`;

export const kFloat = keyframes`
  0%, 100% { transform: translateY(0px);  }
  50%       { transform: translateY(-8px); }
`;

export const kPulseRing = keyframes`
  0%   { transform: scale(0.95); box-shadow: 0 0 0 0   ${COLORS.primaryGlow}; }
  70%  { transform: scale(1);    box-shadow: 0 0 0 12px rgba(61,107,94,0);    }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0   rgba(61,107,94,0);    }
`;

export const kShimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

export const kRotate = keyframes`
  from { transform: rotate(0deg);   }
  to   { transform: rotate(360deg); }
`;

// ─── Section ───────────────────────────────────────────────────────────────────

export const Section = styled.section`
  /* ── All color tokens now come from colors.js (INDIGO_CSS_VARS) ── */
  ${INDIGO_CSS_VARS}

  --r:    1rem;
  --r-lg: 1.6rem;
  --r-xl: 2.4rem;

  --s-sm: 0 2px 14px rgba(0,0,0,0.07);
  --s:    0 4px 28px rgba(0,0,0,0.10);
  --s-lg: 0 8px 48px rgba(0,0,0,0.14);
  --s-xl: 0 16px 64px rgba(0,0,0,0.18);

  --t:      0.22s ease;
  --t-slow: 0.40s ease;

  position: relative;
  width: 100%;
  background-color: ${COLORS.pageBg};
  overflow: hidden;
  padding-bottom: 5rem;
  margin-top: 0;
`;
Section.displayName = "Section";

/* Decorative background blobs */
export const BgBlob = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;

  &.b1 {
    width: 600px; height: 600px;
    top: -200px; right: -180px;
    background: radial-gradient(circle, ${COLORS.primary} 0%, transparent 68%);
    opacity: 0.06; filter: blur(70px);
  }
  &.b2 {
    width: 400px; height: 400px;
    bottom: -100px; left: -120px;
    background: radial-gradient(circle, ${COLORS.primaryLight} 0%, transparent 68%);
    opacity: 0.07; filter: blur(60px);
  }
  &.b3 {
    width: 280px; height: 280px;
    top: 35%; left: 42%;
    background: radial-gradient(circle, ${COLORS.accent} 0%, transparent 68%);
    opacity: 0.05; filter: blur(50px);
  }
  @media (max-width: 48em) {
    &.b1 { width: 300px; height: 300px; }
    &.b2 { width: 220px; height: 220px; }
    &.b3 { display: none; }
  }
`;
BgBlob.displayName = "BgBlob";

/* Decorative dotted grid overlay */
export const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: radial-gradient(circle, ${COLORS.primaryTint} 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.5;
`;
DotGrid.displayName = "DotGrid";

// ─── Inner wrapper ────────────────────────────────────────────────────────────

export const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 48em) { padding: 0 2rem; }
  @media (min-width: 64em) { padding: 0 3rem; }
`;
Inner.displayName = "Inner";

// ─── Header ───────────────────────────────────────────────────────────────────

export const HeaderWrap = styled.div`
  text-align: center;
  padding-top: 2rem;
  padding-bottom: 3rem;

  /* gradient accent bar below title — matches site-wide header style */
  &::after {
    content: "";
    display: block;
    width: 56px;
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(90deg, #3d6b4f 0%, #7a9e4e 50%, #c9a84c 100%);
    margin: 1rem auto 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.7s ease both;
  }
  @media (min-width: 48em) { padding-top: 3rem; padding-bottom: 3.5rem; }
`;
HeaderWrap.displayName = "HeaderWrap";

export const Eyebrow = styled.span`
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #3d6b4f;
  margin-bottom: 0.75rem;
`;
Eyebrow.displayName = "Eyebrow";

export const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5.5vw, 3.2rem);
  font-weight: 800;
  color: #2e1f0f;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0;
`;
SectionTitle.displayName = "SectionTitle";

export const SectionDesc = styled.p`
  font-size: clamp(0.85rem, 2.2vw, 0.98rem);
  color: var(--c-text);
  max-width: 660px;
  margin: 1.5rem auto 0;
  line-height: 1.88;
  opacity: 0.7;
`;
SectionDesc.displayName = "SectionDesc";

/* Kept for JSX compatibility — replaced visually by HeaderWrap::after */
export const Divider = styled.div`
  display: none;
`;
Divider.displayName = "Divider";

// ─── Steps strip ─────────────────────────────────────────────────────────────

export const StepsStrip = styled.div`
  display: none;

  @media (min-width: 48em) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    margin-bottom: 3.5rem;
    padding: 0 1rem;

    @media (prefers-reduced-motion: no-preference) {
      animation: ${kFadeUp} 0.6s ease both 0.15s;
    }
  }
`;
StepsStrip.displayName = "StepsStrip";

export const StepDot = styled.div`
  width: 36px; height: 36px;
  border-radius: 50%;
  background: ${({ $active }) =>
    $active
      ? `linear-gradient(135deg, var(--c-accent), var(--c-accent-2))`
      : "var(--c-surface)"};
  border: 2px solid ${({ $active }) => $active ? "var(--c-accent)" : "var(--c-glow)"};
  color: ${({ $active }) => $active ? "#fff" : "var(--c-accent)"};
  font-size: 0.78rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: ${({ $active }) =>
    $active ? `0 4px 14px ${COLORS.primaryShadow}` : "var(--s-sm)"};
  transition: all var(--t);
`;
StepDot.displayName = "StepDot";

export const StepLine = styled.div`
  flex: 1;
  height: 2px;
  max-width: 80px;
  background: linear-gradient(90deg, var(--c-accent), var(--c-glow));
  opacity: 0.35;
`;
StepLine.displayName = "StepLine";

// ─── Cards grid ───────────────────────────────────────────────────────────────

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem 1.5rem;

  @media (min-width: 36em) { grid-template-columns: repeat(2, 1fr); }
  @media (min-width: 64em) { grid-template-columns: repeat(3, 1fr); }
`;
CardsGrid.displayName = "CardsGrid";

// ─── Card ─────────────────────────────────────────────────────────────────────

export const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--c-surface);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid var(--c-border);
  border-radius: var(--r-xl);
  padding: 5rem 1.8rem 2rem;
  box-shadow: var(--s);
  cursor: pointer;
  transition: transform var(--t-slow), box-shadow var(--t-slow),
              border-color var(--t);
  text-align: center;
  margin-top: 3.5rem;

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--s-xl);
    border-color: var(--c-border-accent);
  }
  &:active { transform: translateY(-3px); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 4px; }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.55s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 120, 480)}ms;
  }
`;
Card.displayName = "Card";

/* Step number chip — top-left corner of each card */
export const StepChip = styled.span`
  position: absolute;
  top: 1rem;
  left: 1.2rem;
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--c-accent);
  background: var(--c-tint);
  border: 1px solid var(--c-glow);
  border-radius: 999px;
  padding: 0.18rem 0.6rem;
`;
StepChip.displayName = "StepChip";

/* Quote mark decoration */
export const QuoteMark = styled.span`
  position: absolute;
  top: 1rem;
  right: 1.2rem;
  font-size: 3rem;
  font-family: Georgia, serif;
  line-height: 1;
  color: var(--c-accent);
  opacity: 0.12;
  pointer-events: none;
  user-select: none;
`;
QuoteMark.displayName = "QuoteMark";

// ─── Avatar ───────────────────────────────────────────────────────────────────

export const AvatarRing = styled.div`
  position: absolute;
  top: -3.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--c-accent), var(--c-accent-2));
  padding: 3px;
  box-shadow: 0 6px 24px ${COLORS.primaryGlow};

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFloat} 4s ease-in-out infinite;
    animation-delay: ${({ $index }) => $index * 0.7}s;
  }
`;
AvatarRing.displayName = "AvatarRing";

export const AvatarInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${COLORS.white};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
AvatarInner.displayName = "AvatarInner";

export const AvatarImg = styled.img`
  width: 58px;
  height: 58px;
  object-fit: contain;
  display: block;
`;
AvatarImg.displayName = "AvatarImg";

export const AvatarFallback = styled.div`
  width: 58px;
  height: 58px;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 1.65rem;

  background: linear-gradient(
    135deg,
    var(--c-tint),
    var(--c-surface)
  );

  border-radius: 50%;
`;
AvatarFallback.displayName = "AvatarFallback";

// ─── Card content ─────────────────────────────────────────────────────────────

export const CardTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--c-accent);
  margin: 0 0 0.9rem;
  line-height: 1.25;
`;
CardTitle.displayName = "CardTitle";

export const CardText = styled.p`
  font-size: 0.87rem;
  color: var(--c-text);
  line-height: 1.78;
  opacity: 0.75;
  margin: 0 0 1.6rem;
  flex: 1;
`;
CardText.displayName = "CardText";

/* Star rating row */
export const Stars = styled.div`
  display: flex;
  gap: 0.2rem;
  margin-bottom: 1.4rem;
  justify-content: center;

  span {
    font-size: 0.9rem;
    color: ${COLORS.star};
  }
`;
Stars.displayName = "Stars";

// ─── CTA button ───────────────────────────────────────────────────────────────

export const CtaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.5rem;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, var(--c-accent), var(--c-accent-2));
  color: ${COLORS.white};
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  min-height: 44px;
  white-space: nowrap;
  box-shadow: 0 4px 16px ${COLORS.primaryGlow};
  transition: transform var(--t), box-shadow var(--t), filter var(--t);

  svg { width: 14px; height: 14px; flex-shrink: 0; transition: transform var(--t); }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px ${COLORS.primaryGlow};
    filter: brightness(1.08);
  }
  &:hover svg { transform: translateX(3px); }
  &:active { transform: translateY(0) scale(0.97); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 3px; }
`;
CtaBtn.displayName = "CtaBtn";

// ─── Bottom CTA banner ────────────────────────────────────────────────────────

export const CtaBanner = styled.div`
  margin-top: 5rem;
  border-radius: var(--r-xl);
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 60%, ${COLORS.accentDark} 100%);
  padding: 3rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 48px ${COLORS.primaryGlow};

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kScaleIn} 0.6s ease both 0.3s;
  }
  @media (min-width: 48em) { padding: 4rem 3rem; }
`;
CtaBanner.displayName = "CtaBanner";

export const BannerOrb = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;

  &.o1 {
    width: 300px; height: 300px;
    top: -100px; right: -80px;
    background: rgba(255,255,255,0.08);
  }
  &.o2 {
    width: 200px; height: 200px;
    bottom: -80px; left: -60px;
    background: rgba(255,255,255,0.06);
  }
`;
BannerOrb.displayName = "BannerOrb";

export const BannerTitle = styled.h3`
  font-size: clamp(1.4rem, 4vw, 2.2rem);
  font-weight: 900;
  color: ${COLORS.white};
  letter-spacing: -0.025em;
  margin: 0 0 0.8rem;
  line-height: 1.15;
`;
BannerTitle.displayName = "BannerTitle";

export const BannerDesc = styled.p`
  font-size: clamp(0.82rem, 2vw, 0.95rem);
  color: rgba(253,250,246,0.82);
  max-width: 520px;
  margin: 0 auto 2rem;
  line-height: 1.75;
`;
BannerDesc.displayName = "BannerDesc";

export const BannerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 2.2rem;
  border-radius: 999px;
  border: 2px solid rgba(253,250,246,0.6);
  background: rgba(253,250,246,0.15);
  color: ${COLORS.white};
  font-size: 0.92rem;
  font-weight: 800;
  cursor: pointer;
  min-height: 50px;
  backdrop-filter: blur(10px);
  transition: background var(--t), border-color var(--t),
              transform var(--t), box-shadow var(--t);

  svg { width: 16px; height: 16px; transition: transform var(--t); }

  &:hover {
    background: rgba(253,250,246,0.28);
    border-color: rgba(253,250,246,0.9);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  }
  &:hover svg { transform: translateX(4px); }
  &:active { transform: translateY(0) scale(0.97); }
  &:focus-visible { outline: 2px solid ${COLORS.white}; outline-offset: 3px; }
`;
BannerBtn.displayName = "BannerBtn";